import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isStoreBuild } from '@/lib/platform';
import { APP_BRAND, IS_APP_BUILD } from '@/lib/appBrand';
import { DELETE_ACCOUNT, PRIVACY, SUPPORT, TERMS, type LegalDoc } from './legalTexts';

/**
 * Страница юридического документа — политики или условий.
 *
 * Отдельная публичная страница, а не PDF и не файл в облаке: и Apple, и Google
 * требуют РАБОЧУЮ ссылку на политику при подаче приложения, и проверяют её
 * открытием. Ссылка на скачивание файла для этого не годится.
 *
 * ЯЗЫК. По умолчанию платформа открывается на казахском. Для ревьюера Apple
 * или Google это тупик: он заходит по ссылке из чистого браузера, где выбор
 * языка ещё не сохранён, видит казахский текст и не может подтвердить, что
 * политика вообще про то приложение, которое он проверяет. Поэтому здесь два
 * способа выбрать язык, не зависящие от настроек платформы:
 *  - `?lang=en` в адресе — такую ссылку и указываем в кабинетах магазинов;
 *  - переключатель в шапке — для обычного посетителя сайта.
 * Выбор языка НЕ меняет язык платформы: человек, открывший политику
 * по-английски, не должен обнаружить, что у него переключился весь интерфейс.
 */

const LANGS = [
  { code: 'kk', label: 'Қаз' },
  { code: 'ru', label: 'Рус' },
  { code: 'en', label: 'Eng' },
] as const;

function resolve(docs: Record<string, LegalDoc>, lang: string): string {
  const short = lang.split('-')[0];
  // Русский как запасной: пустая страница вместо документа — отказ на ревью.
  return docs[short] ? short : 'ru';
}

export function LegalLayout({ docs }: { docs: Record<string, LegalDoc> }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Адрес важнее настройки интерфейса: ссылку с `?lang=` дают намеренно.
  const [lang, setLang] = useState(() => params.get('lang') ?? i18n.language);
  const current = resolve(docs, lang);
  const doc = docs[current];

  // ⚠️ ДОКУМЕНТ ОТКРЫВАЕТСЯ С НАЧАЛА (25.09.2026, сборка 61). Страница живёт на прокрутке окна, а
  // переход из настроек приложения («Документы» внизу экрана) эту прокрутку сохранял: политика
  // открывалась с середины — без заголовка, без «Назад» и без выбора языка. Найдено на симуляторе.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [docs]);

  function choose(code: string) {
    setLang(code);
    // Адрес обновляем, чтобы ссылку можно было скопировать и переслать.
    const next = new URLSearchParams(params);
    next.set('lang', code);
    setParams(next, { replace: true });
  }

  // Показываем только те языки, которые у документа есть. У оферты английской
  // версии нет — договор составлял юрист, и переводить его самим нельзя.
  // Кнопка на несуществующий язык молча показывала бы русский текст.
  const available = LANGS.filter(({ code }) => docs[code]);

  /**
   * «Назад» — только в приложении (владелец 16.09.2026, сборка 56).
   *
   * ЗАЧЕМ. На айфоне нет системной кнопки «назад», а панели браузера в приложении нет. В сборке 55
   * политика и условия открывались с экрана входа и регистрации, и выйти обратно было нечем — только
   * логотипом, о котором не догадаться. Ревьюер Apple открывает политику первым делом и застрял бы
   * (отказ по 2.1). На сайте кнопки нет: там есть браузерная «назад», шапка остаётся прежней.
   *
   * `history.state.idx` — номер записи react-router: больше нуля, если в приложении было куда
   * вернуться (регистрация, вход, настройки). `history.length` для этого не годится — в нём и
   * записи «вперёд». Открытая первой страница ведёт на главную, иначе кнопка молча ничего не сделает.
   */
  const inApp = isStoreBuild();
  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FFFDFA] text-on-surface">
      <header
        className="border-b border-[#F0E6D8] px-5 py-4 md:px-8"
        style={{ paddingTop: 'calc(1rem + var(--sa-top))' }}
      >
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
          {inApp && (
            <button
              type="button"
              onClick={goBack}
              className="-ml-2 flex h-11 shrink-0 items-center gap-0.5 rounded-full pl-1 pr-3 text-[15px] font-semibold text-primary transition hover:bg-[#FFF3E3] active:scale-95"
            >
              <span className="material-symbols-outlined text-[26px] leading-none">chevron_left</span>
              {t('common.back')}
            </button>
          )}
          <Link to="/" className="mr-auto text-[20px] font-extrabold tracking-tight text-primary">
            {/* В store-сборке — APP_BRAND, OneBilim Academy с 1.1.1 (lib/appBrand.ts); сайт — прежний onebilim. */}
            {IS_APP_BUILD ? APP_BRAND : 'onebilim'}
          </Link>
          <div className="flex items-center gap-1">
            {available.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                onClick={() => choose(code)}
                aria-current={current === code ? 'true' : undefined}
                className={
                  current === code
                    ? 'rounded-full bg-primary px-3 py-1.5 text-[13px] font-semibold text-white'
                    : 'rounded-full px-3 py-1.5 text-[13px] font-medium text-[#A99C8C] hover:bg-[#F5EFE6] hover:text-[#1A1512]'
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main
        className="mx-auto max-w-3xl px-5 py-10 md:px-8 md:py-14"
        style={{ paddingBottom: 'calc(3rem + var(--sa-bottom))' }}
      >
        {/* Заголовок оферты — длинный и целиком заглавными («БІЛІМ БЕРУ
            ҚЫЗМЕТТЕРІН КӨРСЕТУГЕ АРНАЛҒАН ОФЕРТА»). В том же кегле, что
            «Privacy Policy», он занимает пол-экрана, поэтому длинные названия
            набираем мельче. */}
        <h1
          className={
            doc.title.length > 34
              ? 'text-[22px] font-extrabold leading-snug tracking-[-.01em] text-[#1A1512] md:text-[28px]'
              : 'text-[30px] font-extrabold leading-tight tracking-[-.02em] text-[#1A1512] md:text-[40px]'
          }
        >
          {doc.title}
        </h1>
        {/* У оферты даты редакции нет: в оригинале на её месте графа бумажного
            бланка («г. Астана « »____2026 год»), которую на сайте некому
            заполнить. Пустой абзац оставил бы под заголовком лишний отступ. */}
        {doc.updated ? <p className="mt-2 text-[13.5px] text-[#A99C8C]">{doc.updated}</p> : null}
        <p className="mt-6 text-[16px] leading-relaxed text-on-surface-variant">{doc.intro}</p>

        <div className="mt-10 space-y-9">
          {doc.sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-[19px] font-bold tracking-[-.01em] text-[#1A1512]">{section.title}</h2>
              <div className="mt-3 space-y-2.5">
                {section.body.map((paragraph, i) => (
                  <p key={i} className="text-[15px] leading-relaxed text-on-surface-variant">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}

export function PrivacyPolicyPage() {
  return <LegalLayout docs={PRIVACY} />;
}

export function TermsPage() {
  return <LegalLayout docs={TERMS} />;
}

/**
 * Публичная страница удаления аккаунта. Google Play требует адрес, доступный
 * БЕЗ входа в приложение: ревьюер открывает его из чистого браузера и должен
 * увидеть порядок удаления, состав удаляемых данных и сроки.
 */
export function DeleteAccountPage() {
  return <LegalLayout docs={DELETE_ACCOUNT} />;
}

/**
 * Публичная страница поддержки.
 *
 * ⚠️ В кабинете магазина в поле «ссылка поддержки» должна стоять ИМЕННО ОНА, а
 * не главная сайта. Проверяющий Apple открывает эту ссылку всегда; до 31.08.2026
 * там стоял адрес витрины, и он попал на страницу продаж курсов — снимок именно
 * оттуда был приложен к отказу по правилу 3.1.1.
 */
export function SupportPage() {
  return <LegalLayout docs={SUPPORT} />;
}
